/* eslint-disable react-hooks/exhaustive-deps */
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import GoogleFontLoader from "react-google-font-loader";
import { AppBar, Avatar, Box, ButtonBase, CssBaseline, Menu as MuiMenu, MenuItem, Slide, Toolbar, Typography, useScrollTrigger, Alert, AlertTitle } from "@mui/material";
import { Editor } from "@craftjs/core";
import { db } from "@/firebase";
import { onSnapshot, doc } from "firebase/firestore";
import lz from "lzutf8";
import { Style } from "react-style-tag";
import Canvas, { Section } from "@/features/dine-in-flow/Canvas";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useAppDispatch } from "@/hooks";
import MenuSkeleton from "@/features/dine-in-flow/Skeleton";
import { deleteOrder, updateOrder } from "@/features/order/slice";
import RenderNode from "@/features/dine-in-flow/RenderNode";
import fonts from "@/constants/fonts";
import { logOut } from "@/features/auth/slice";
import MenuBottomNavigation from "@/components/MenuBottomNavigation";
import ContainerBlock from "@/components/editor/blocks/ContainerBlock";
import TextBlock from "@/components/editor/blocks/TextBlock";
import ImageBlock from "@/components/editor/blocks/ImageBlock";
import VerticalSpacerBlock from "@/components/editor/blocks/VerticalSpacerBlock";
import DishBlock from "@/components/editor/blocks/DishBlock";
import DropRegionBlock from "@/components/editor/blocks/DropRegionBlock";
import SectionBlock from "@/components/editor/blocks/section-block/SectionBlock";
import AppConfig from "@/components/AppConfig";
import { Configs } from "@/constants/configs";
import useAccessControl from "@/features/access-control/useAccessControl";
import { EyeOffIcon } from "lucide-react";
import NotFound from "@/features/dine-in-flow/components/NotFound";
import { useFetchTemplateByIdQuery } from "@/features/templates/templatesApi";
import { Order } from "@/types/orders";
import { Menu, Template } from "@/types";
import { WebsiteContext } from "../website/WebsiteProvider";
import { useFetchMenuByIdQuery } from "@/features/organizations/api/menusApi";
import { useFetchContentQuery } from "@/features/organizations/api/contentApi";
import ProductDetails from "@/features/product-details/ProductDetails";
import RegionBlock from "@/components/editor/blocks/RegionBlock";
import "./Menu.scss";

interface HeaderProps {
  title: string;
}

const HideOnScroll = (props: any) => {
  const { children } = props;

  const trigger = useScrollTrigger({
    threshold: 100,
  });

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
};

interface IMenuContext {
  menu: Menu | null;
  template: Template | null;
  sections: Section[];
}

export const MenuContext = createContext<IMenuContext>({
  menu: null,
  template: null,
  sections: [],
});

const Header = (props: HeaderProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { organizationId } = useParams<{
    organizationId: string;
  }>();

  const [openUserMenu, setOpenUserMenu] = useState(false);

  const handleMenu = (event: any) => {
    setOpenUserMenu(event.currentTarget);
  };

  const handleClose = () => {
    setOpenUserMenu(false);
  };

  const handleLogOut = () => {
    dispatch(logOut());
    navigate(`/comercios/${organizationId}`);
  };

  return (
    <div className="menu-viewer__header">
      <CssBaseline />
      <HideOnScroll>
        <AppBar elevation={2} color="default">
          <Toolbar>
            <Typography variant="h4" component="div" sx={{ flexGrow: 1 }} color={"black"}>
              {props.title}
            </Typography>
            <div>
              <AppConfig isEnabled={Configs.DRAWER_SIGNIN}>
                <ButtonBase onClick={handleMenu}>
                  <Avatar src="/placeholder.png" sx={{ height: "40px", width: "40px" }} />
                </ButtonBase>
                <MuiMenu
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  open={openUserMenu}
                  onClose={handleClose}
                >
                  <MenuItem onClick={handleLogOut}>Cerrar Sesión</MenuItem>
                </MuiMenu>
              </AppConfig>
            </div>
          </Toolbar>
        </AppBar>
      </HideOnScroll>
    </div>
  );
};

const MenuReader = () => {
  const dispatch = useAppDispatch();
  const [json, setJson] = useState();
  const [sections, setSections] = useState<Section[]>([]);
  const { search } = useLocation();
  const { menuId } = useParams<{ menuId: string }>();
  const { organization } = useContext(WebsiteContext);
  const { isActiveUserAdmin } = useAccessControl();

  const contentQuery = useFetchContentQuery(organization?.id ?? skipToken, {
    skip: !organization?.id,
  });

  const menuQuery = useFetchMenuByIdQuery(
    { menuId: menuId ?? skipToken, organizationId: organization?.id ?? skipToken },
    {
      skip: !menuId || !organization?.id,
    }
  );

  const templateQuery = useFetchTemplateByIdQuery(menuQuery?.data?.template || skipToken, {
    skip: !menuQuery?.data?.template,
  });

  useEffect(() => {
    const qs = new URLSearchParams(search);
    const tableId = qs.get("mesa");

    if (!organization?.id || !tableId) return;

    // Subscribe to orders
    dispatch({ type: "orders/subscribeToOrdersByTable" });
    const docRef = doc(db, `organizations/${organization?.id}/orders/${tableId}`);

    const unsubscribe = onSnapshot(
      docRef,
      (doc) => {
        if (doc.exists()) {
          dispatch(
            updateOrder({
              ...(doc.data() as Order),
              id: doc.id,
            })
          );
        } else {
          dispatch(deleteOrder());
        }
      },
      (error) => {
        console.error(error);
      }
    );

    return () => {
      unsubscribe();
      dispatch({ type: "orders/unsubscribeToOrdersByTable" });
    };
  }, [search]);

  const sectionList = useMemo(() => {
    // TODO: Fix index menu
    return [];
  }, [templateQuery?.data]);

  useEffect(() => {
    if (menuQuery?.data?.data || templateQuery?.data?.data) {
      const json = lz.decompress(lz.decodeBase64(menuQuery?.data?.data ?? templateQuery?.data?.data ?? ""));
      setJson(json);
    }
  }, [menuQuery?.data?.data, templateQuery.data?.data]);

  if (templateQuery.isLoading || contentQuery.isLoading) {
    return <MenuSkeleton />;
  }

  if (!menuQuery?.data?.isPublished && !isActiveUserAdmin) {
    return <NotFound />;
  }

  if (!json) return null;

  return (
    <MenuContext.Provider
      value={{
        menu: menuQuery?.data ?? null,
        template: templateQuery?.data ?? null,
        sections,
      }}
    >
      <Box
        className="menu-viewer"
        sx={{
          overflow: "scroll",
          borderSizing: "border-box",
          paddingTop: !menuQuery?.data?.isPublished && isActiveUserAdmin ? 0 : "55px",
        }}
      >
        <GoogleFontLoader fonts={fonts} />

        {!menuQuery?.data?.isPublished && isActiveUserAdmin ? (
          <Box padding={2}>
            <Alert icon={<EyeOffIcon />} severity="warning">
              <AlertTitle>No publicado</AlertTitle>
              Menú no publicado. Solo tu puedes verlo por ser administrador.
            </Alert>
          </Box>
        ) : (
          <Header title={menuQuery?.data?.title ?? ""} />
        )}
        <Editor
          resolver={{
            SectionBlock,
            ContainerBlock,
            TextBlock,
            ImageBlock,
            VerticalSpacerBlock,
            DishBlock,
            DropRegionBlock,
            RegionBlock,
          }}
          enabled={false}
          onRender={RenderNode}
        >
          <Style className={"viewport"} children={templateQuery.data?.styles ?? ""} />
          <Canvas json={json} setSections={setSections} />
        </Editor>
        <ProductDetails />
        <div className="menu-viewer__navigation">
          <MenuBottomNavigation sections={sectionList as Section[]} />
        </div>
      </Box>
    </MenuContext.Provider>
  );
};

export default MenuReader;
